import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import PageIntroduction from '../../Shared/PageIntroduction';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import type IValidatable from '../../../Shared/validatable.interface';
import ValidationInfo from '../../../../validation/validation-info';
import Breadcrumb from '../Breadcrumb';
import useFetch, { Status } from '../../../../hooks/fetch.hook';
import { UserApiModel } from '../../models/user.api-model';
import type { Role } from '../../../../models/pa-egna-ben/role.enum';
import Spinner from '../../../Shared/Spinner';
import Alert from '../../../Shared/Alert';
import ValidationSummary from '../../../Shared/ValidationSummary';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import InformationBox from '../../Shared/InformationBox';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';
import AntiforgeryService from '../../../../services/anti-forgery.service';
import { USER_ID_NOT_AVAILABLE } from '../../../../models/pa-egna-ben/user.model';

function MyInformation({ viewerRole }: { viewerRole: Role }) {
  // === STATE ===
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);
  const [getCurrentUser] = useFetch<UserApiModel>(`/api/paegnaben/${viewerRole}/current`);
  const [firstNameState, setFirstNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [lastNameState, setLastNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [emailAddressNameState, setEmailAddressState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [phoneNumberState, setPhoneNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });

  // === REFS ===
  const firstNameRef = useRef<IValidatable>(null!);
  const lastNameRef = useRef<IValidatable>(null!);
  const emailAddressRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  function getValidatableComponents(): {
    component: IValidatable;
    validationInfo: ValidationInfo;
    setState: React.Dispatch<React.SetStateAction<{ value: unknown; validationInfo: ValidationInfo }>>;
  }[] {
    const result: ValidatableComponent[] = [
      { component: firstNameRef.current, validationInfo: firstNameState.validationInfo, setState: setFirstNameState },
      { component: lastNameRef.current, validationInfo: lastNameState.validationInfo, setState: setLastNameState },
      { component: emailAddressRef.current, validationInfo: emailAddressNameState.validationInfo, setState: setEmailAddressState },
      { component: phoneNumberRef.current, validationInfo: phoneNumberState.validationInfo, setState: setPhoneNumberState },
    ];
    return result;
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, AnalyticsService.ErrorAction, e);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSaveButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;
      setSaveErrorCode(undefined);
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }
      setSaveStatus(Status.loading);
      const userApiModel: UserApiModel = {
        id: getCurrentUser.data ? getCurrentUser.data.id : USER_ID_NOT_AVAILABLE,
        firstName: firstNameState.value,
        lastName: lastNameState.value,
        emailAddress: emailAddressNameState.value,
        phoneNumber: phoneNumberState.value,
      };
      try {
        const response = await fetch(`/api/paegnaben/${viewerRole}/current`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
          body: JSON.stringify(userApiModel),
        });
        if (response.ok) {
          setSaveStatus(Status.success);
          setSaveErrorCode(undefined);
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Användare uppdaterad');
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Användare uppdaterad - misslyckat');
        }
      } catch {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Användare uppdaterad - misslyckat');
        setSaveStatus(Status.failed);
      }
    });
    if (!isFormValid || saveErrorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EFFECTS ===
  useEffect(() => {
    const user = getCurrentUser.data ? UserApiModel.mapToBusinessModel(getCurrentUser.data) : undefined;
    if (user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFirstNameState({ value: user.firstName, validationInfo: new ValidationInfo() });
      setLastNameState({ value: user.lastName, validationInfo: new ValidationInfo() });
      setEmailAddressState({ value: user.emailAddress, validationInfo: new ValidationInfo() });
      setPhoneNumberState({ value: user.phoneNumber, validationInfo: new ValidationInfo() });
    }
  }, [getCurrentUser.data]);

  // === RENDER ===
  if (saveStatus === Status.success) {
    return <Navigate to="/" />;
  }
  if (getCurrentUser.httpResponseStatusCode === 401 || (saveStatus === Status.failed && saveErrorCode === 401)) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getCurrentUser.httpResponseStatusCode === 403 || (saveStatus === Status.failed && saveErrorCode === 403)) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { text: 'Mina sidor', url: '/' },
          { text: 'Mina uppgifter', url: undefined },
        ]}
      />
      <PageIntroduction heading="Mina uppgifter" />
      {getCurrentUser.status === Status.loading && <Spinner />}
      {getCurrentUser.status === Status.failed && <Alert show={true} heading="Det gick inte att hämta dina uppgifter. Försök igen senare" closable={false} />}

      {getCurrentUser.status === Status.success && (
        <form id="form">
          <fieldset>
            <legend className="visually-hidden">Formulär för uppdatera mina uppgifter</legend>
            <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
            <TextFieldFormGroup
              ref={firstNameRef}
              labelText="Förnamn"
              required={true}
              hideRequiredAsterisk={true}
              inputId="first-name-input"
              type="text"
              serverValidationName="firstName"
              state={[firstNameState, setFirstNameState]}
              disabled={getCurrentUser.data?.id === USER_ID_NOT_AVAILABLE}
            />
            <TextFieldFormGroup
              ref={lastNameRef}
              labelText="Efternamn"
              required={true}
              hideRequiredAsterisk={true}
              inputId="last-name-input"
              type="text"
              serverValidationName="lastName"
              state={[lastNameState, setLastNameState]}
              disabled={getCurrentUser.data?.id === USER_ID_NOT_AVAILABLE}
            />
            <TextFieldFormGroup
              ref={emailAddressRef}
              labelText="E-postadress"
              required={true}
              hideRequiredAsterisk={true}
              disabled={true}
              inputId="email-address-input"
              type="text"
              serverValidationName="emailAddress"
              state={[emailAddressNameState, setEmailAddressState]}
            />
            <TextFieldFormGroup
              ref={phoneNumberRef}
              labelText="Telefonnummer"
              inputId="phone-number-input"
              type="text"
              serverValidationName="phoneNumber"
              state={[phoneNumberState, setPhoneNumberState]}
              disabled={getCurrentUser.data?.id === USER_ID_NOT_AVAILABLE}
            />
            {getCurrentUser.data?.id !== USER_ID_NOT_AVAILABLE && (
              <InformationBox
                type={InformationBoxType.Default}
                textElement={
                  <div>
                    <div className="h4 mb-0">Vill du avsluta ditt konto?</div>
                    <Link to="/avsluta-konto">Avsluta konto</Link>
                  </div>
                }
                className="mb-3"
              />
            )}
            <div className="d-grid gap-0">
              <button type="button" className="btn btn-primary" onClick={handleSaveButtonClick} disabled={getCurrentUser.data?.id === USER_ID_NOT_AVAILABLE}>
                Spara ändringar
              </button>
            </div>
            {saveStatus === Status.failed && saveErrorCode !== 400 && (
              <Alert show={true} heading="Kunde inte spara dina uppgifter. Försök igen senare." closable={false} className="mt-3" />
            )}
          </fieldset>
        </form>
      )}
    </div>
  );
}

export default MyInformation;